/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { useRef, useState, useEffect, useCallback } from "react";
import {
  AwvInfo,
  BodyGoHome,
  ControlApi,
  MapWayNodeType,
  RemotePositionInfo,
  TasksApi,
} from "client";
import { useApiWithTimeout } from "./ApiWrapperWithTimeout";
import { Const } from "const";
import { useMapDataContext } from "context/MapDataProvider";
import { useCallApi } from "./CallApiHook";
import moment from "moment";
import { getElapsedTime } from "utils/DateTime";
import { TimeoutError } from "exception/TimeoutError";
import { getNodeType } from "components/Map/NodeMapOverlay";
import { useAwvDataContext } from "context/AwvDataProvider";

/**
 * Create GoHome task processing.
 * @param {number} jobSetStatus
 * @return {boolean} processing(true) or finished(false)
 */
function isProcessingGoHomeTask(jobSetStatus: number): boolean {
  // 1: wait process start
  // 2: processing
  return [1, 2].includes(jobSetStatus);
}

/**
 * Get Home nodeId.
 * @param {MapWayNodeType[]} mapInfo Map information
 * @return {number | undefined} Home nodeId
 */
function getHomeNodeId(mapInfo: MapWayNodeType[]): number | undefined {
  return mapInfo
    .flatMap((floor) => floor.nodes)
    .find((node) => {
      const nodeType = getNodeType(node?.description);
      return nodeType.type === "NamedNode" && nodeType.label === "Home";
    })?.id;
}

/**
 * Get nearest nodeId.
 * @param {MapWayNodeType[]} mapInfo
 * @param {RemotePositionInfo} awvPosition
 * @return {number | undefined} Nearest nodeId
 */
function getNearestNodeId(
  mapInfo: MapWayNodeType[],
  awvPosition?: RemotePositionInfo
): number | undefined {
  interface IdDistancePair {
    id: number;
    distance: number;
  }
  const id = mapInfo
    .flatMap((m) => m.nodes)
    .reduce<IdDistancePair>(
      (acc, node) => {
        if (getNodeType(node?.description).type === "LandmarkNode") {
          // No LandmarkNode
          return acc;
        }
        const nodePosition = {
          latitude: node?.geolocation?.[1],
          longitude: node?.geolocation?.[0],
        };
        if (
          awvPosition &&
          awvPosition.latitude &&
          awvPosition.longitude &&
          nodePosition.latitude &&
          nodePosition.longitude
        ) {
          const distance =
            Math.pow(awvPosition.latitude - nodePosition.latitude, 2) +
            Math.pow(awvPosition.longitude - nodePosition.longitude, 2);
          if (acc.distance > distance && node?.id !== undefined) {
            return { id: node.id, distance: distance };
          }
        }
        return acc;
      },
      { id: -1, distance: Infinity }
    ).id;
  return id === -1 ? undefined : id;
}

export const useGoHomeTask = () => {
  const { mapData } = useMapDataContext();
  const [controlApi, cancelControlApi] = useApiWithTimeout(
    ControlApi,
    Const.operationTimeout.goHomeTask
  );
  const [getTaskCreationProgressApi, cancelGetTaskCreationProgressApi] =
    useApiWithTimeout(TasksApi, Const.operationTimeout.getTaskCreationProgress);
  const [getTaskApi, cancelGetTaskApi] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.getTask
  );
  const timerIdRef = useRef<NodeJS.Timeout | string | number | undefined>();
  const pollingStartTime = useRef(new Date());
  const [awvInfo, setAwvInfo] = useState<AwvInfo>();

  const { awvData } = useAwvDataContext();
  const awvRef = useRef(awvInfo);
  useEffect(() => {
    // latest condition
    awvRef.current = awvData.awvInfos?.find(
      (awv) => awv.frameTypeAndNumber === awvInfo?.frameTypeAndNumber
    );
  }, [awvData.awvInfos, awvInfo]);

  const cancelCallback = useCallback(() => {
    cancelControlApi();
    cancelGetTaskCreationProgressApi();
    cancelGetTaskApi();
    clearInterval(timerIdRef.current);
  }, [cancelControlApi, cancelGetTaskCreationProgressApi, cancelGetTaskApi]);

  const pollingCheck = useCallback(
    (jobSetId: string) => {
      pollingStartTime.current = new Date();
      return new Promise<number>((resolve, reject) => {
        let loading = false;
        clearInterval(timerIdRef.current);
        timerIdRef.current = setInterval(async () => {
          if (loading) {
            return;
          }
          loading = true;
          try {
            const res = await getTaskCreationProgressApi.getTaskCreateProgress({
              jobSetId: jobSetId,
            });
            if (!isProcessingGoHomeTask(res.jobSetStatus)) {
              clearInterval(timerIdRef.current);
              resolve(res.jobSetStatus);
            }
            if (
              getElapsedTime(pollingStartTime.current) >
              Const.operationTimeout.goHomeTask
            ) {
              throw new TimeoutError("Go home polling check has timed out");
            }
          } catch (err) {
            clearInterval(timerIdRef.current);
            reject(err);
          } finally {
            loading = false;
          }
        }, Const.taskProgressInterval);
      });
    },
    [getTaskCreationProgressApi]
  );

  const result = useCallApi(
    async () => {
      if (awvInfo?.frameTypeAndNumber) {
        // Find Home node
        const homeId = getHomeNodeId(mapData.mapInfo);
        if (homeId === undefined) {
          throw new Error("Home not found");
        }

        // Find current node
        const nearestNodeId = getNearestNodeId(
          mapData.mapInfo,
          awvRef.current?.position?.positionInfo
        );
        if (nearestNodeId === undefined) {
          throw new Error("Nearest node not found");
        }
        const bodyGoHome: BodyGoHome = {
          frameTypeAndNumber: awvInfo.frameTypeAndNumber,
          mapId: mapData.mapId,
          mapVer: mapData.mapVer,
          toNodeId: homeId,
          fromNodeId: nearestNodeId,
          startDateTime: moment().toDate(),
        };

        const { jobSetId } = await controlApi.createTaskGoHome({
          bodyGoHome: bodyGoHome,
        });
        let { jobSetStatus } =
          await getTaskCreationProgressApi.getTaskCreateProgress({
            jobSetId: jobSetId,
          });
        if (isProcessingGoHomeTask(jobSetStatus)) {
          jobSetStatus = await pollingCheck(jobSetId);
        }
        if (jobSetStatus === 6) {
          // get gohome task.
          const task = await getTaskApi.getTask({
            frameTypeAndNumberList: [awvInfo.frameTypeAndNumber],
            mapId: mapData.mapId,
            jobSetId: jobSetId,
          });
          if (task === undefined) {
            throw new Error("created jobset is not found");
          }
          return task?.taskInfos?.[0];
        } else {
          throw new Error("create jobset failed");
        }
      }
    },
    cancelCallback,
    [
      mapData.mapId,
      mapData.mapVer,
      awvInfo,
      cancelCallback,
      controlApi,
      getTaskApi,
      getTaskCreationProgressApi,
    ]
  );
  return { ...result, setAwvInfo };
};
